import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  deleteDoc,
  onSnapshot,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import ro from "/src/content/ro.json";
import {
  toDateNumber,
  toDisplayDate,
  toIsoDate,
  toStorageDate,
} from "../utils/date";

const normalizeKey = (value) =>
  (value || "").toString().trim().toLowerCase();

const statusClasses = (active, type) => {
  if (!active) return "attendance-pill";
  return type === "prezent"
    ? "attendance-pill is-prezent"
    : "attendance-pill is-absent";
};

const AdminAttendancePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [programOptions, setProgramOptions] = useState([]);
  const [datesByProgram, setDatesByProgram] = useState({});
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [participants, setParticipants] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [attendance, setAttendance] = useState({});
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarSub, setCalendarSub] = useState(null);
  const [calendarStatus, setCalendarStatus] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  // ================= LOAD PROGRAMS + DATES =================
  useEffect(() => {
    const loadPrograms = async () => {
      const snap = await getDocs(collection(db, "stabilire_program"));
      const map = new Map();
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const tip = (data.tipProgram || "").toUpperCase();
        if (!tip) return;
        const date = toStorageDate(data.data || "");
        if (!map.has(tip)) map.set(tip, new Set());
        if (date) map.get(tip).add(date);
      });

      const basePrograms = ["MAI", "MAPN", "ISU", "ANP"];
      const programList = Array.from(
        new Set([...basePrograms, ...map.keys()])
      ).sort();
      const defaultProgram =
        searchParams.get("tip")?.toUpperCase() || programList[0] || "";

      const normalized = {};
      programList.forEach((tip) => {
        const dates = map.get(tip) || new Set();
        normalized[tip] = Array.from(dates).sort((a, b) => {
          const aNum = toDateNumber(a) || 0;
          const bNum = toDateNumber(b) || 0;
          return aNum - bNum;
        });
      });

      setProgramOptions(programList);
      setDatesByProgram(normalized);
      setSelectedProgram(defaultProgram);
      const paramDate = toStorageDate(searchParams.get("data") || "");
      const nextDate = paramDate || (defaultProgram && normalized[defaultProgram]?.[0]) || "";
      setSelectedDate(nextDate);
    };
    loadPrograms();
  }, [searchParams]);

  // ================= LOAD PARTICIPANTS =================
  useEffect(() => {
    const loadParticipants = async () => {
      if (!selectedProgram) return;
      setLoadingList(true);
      try {
        const q = query(
          collection(db, "inscrieri_traseu"),
          where("tipTraseu", "==", selectedProgram)
        );
        const snap = await getDocs(q);
        let arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (selectedDate) {
          const selectedNumber = toDateNumber(selectedDate);
          arr = arr.filter((p) => {
            if (!selectedNumber) return false;
            return toDateNumber(p.dataTraseu || "") === selectedNumber;
          });
        }
        arr.sort((a, b) =>
          (a.numePrenume || a.nume || "").localeCompare(
            b.numePrenume || b.nume || ""
          )
        );
        setParticipants(arr);
      } catch (err) {
        console.error("Eroare la incarcarea participantilor:", err);
        setParticipants([]);
      } finally {
        setLoadingList(false);
      }
    };
    loadParticipants();
  }, [selectedProgram, selectedDate]);

  // ================= LOAD SUBSCRIPTIONS =================
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "abonamente"),
      (snapshot) => {
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setSubscriptions(data);
        setLoadingSubscriptions(false);
      },
      (err) => {
        console.error("Eroare la incarcarea abonamentelor:", err);
        setSubscriptions([]);
        setLoadingSubscriptions(false);
      }
    );
    return () => unsub();
  }, []);

  // ================= LOAD ATTENDANCE =================
  useEffect(() => {
    if (!selectedProgram || !selectedDate) {
      setAttendance({});
      return;
    }
    const selectedNumber = toDateNumber(selectedDate);
    const q = query(
      collection(db, "prezente"),
      where("tipProgram", "==", selectedProgram)
    );
    const unsub = onSnapshot(q, (snap) => {
      const map = {};
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const dateMatch =
          selectedNumber && toDateNumber(data.data || data.date || "") === selectedNumber;
        if (!dateMatch) return;
        const key = normalizeKey(data.userEmail || data.userId || docSnap.id);
        if (!key) return;
        map[key] = data.status;
      });
      setAttendance(map);
    });
    return () => unsub();
  }, [selectedProgram, selectedDate]);

  // ================= ACTIONS =================
  const handleProgramChange = (tip) => {
    setSelectedProgram(tip);
    const firstDate = datesByProgram[tip]?.[0] || "";
    setSelectedDate(firstDate);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const getAttendanceDocId = (participantKey) => {
    const dateKey = toIsoDate(selectedDate);
    if (!dateKey || !selectedProgram || !participantKey) return "";
    return `${selectedProgram}_${dateKey}_${participantKey.replace(/[^a-z0-9]+/gi, "_")}`;
  };

  const upsertAttendance = async (participant, status) => {
    const key = normalizeKey(
      participant.email ||
        participant.userEmail ||
        participant.userId ||
        participant.id
    );
    if (!key || !selectedProgram || !selectedDate) return;

    const docId = getAttendanceDocId(key);
    if (!docId) return;

    const payload = {
      tipProgram: selectedProgram,
      data: toStorageDate(selectedDate),
      dataKey: toIsoDate(selectedDate),
      status,
      userEmail: normalizeKey(participant.email || participant.userEmail),
      userId: participant.uid || participant.userId || participant.id || "",
      userName:
        participant.numePrenume ||
        participant.nume ||
        participant.userName ||
        ro.admin.attendance.participantFallback,
      updatedAt: new Date().toISOString(),
    };

    try {
      await setDoc(doc(db, "prezente", docId), payload);
      setAttendance((prev) => ({ ...prev, [key]: status }));
    } catch (err) {
      console.error("Nu am putut salva prezenta:", err);
      alert(ro.admin.attendance.saveError);
    }
  };

  const clearAttendance = async (participant) => {
    const key = normalizeKey(
      participant.email ||
        participant.userEmail ||
        participant.userId ||
        participant.id
    );
    if (!key || !selectedProgram || !selectedDate) return;
    const docId = getAttendanceDocId(key);
    if (!docId) return;

    try {
      await deleteDoc(doc(db, "prezente", docId));
      setAttendance((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      console.error("Nu am putut sterge prezenta:", err);
      alert(ro.admin.attendance.saveError);
    }
  };

  const titleDate = selectedDate ? toDisplayDate(selectedDate) : "-";

  const titleProgram = selectedProgram || ro.admin.attendance.selectProgramFallback;
  const selectedDateNumber = toDateNumber(selectedDate);

  const activeSubscriptions = useMemo(() => {
    if (!selectedDateNumber) return [];
    return subscriptions.filter((item) => {
      const start = toDateNumber(item.dataInceput || "");
      const end = toDateNumber(item.dataFinal || item.dataExpirare || "");
      if (!start || !end) return false;
      const itemType = (item.tipTraseu || "").toUpperCase();
      if (selectedProgram && itemType !== selectedProgram) return false;
      return selectedDateNumber >= start && selectedDateNumber <= end;
    });
  }, [subscriptions, selectedDateNumber, selectedProgram]);

  const subscriptionKeys = useMemo(() => {
    const keys = new Set();
    activeSubscriptions.forEach((item) => {
      const emailKey = normalizeKey(item.email || item.userEmail);
      const idKey = normalizeKey(item.userId || item.uid || item.id);
      if (emailKey) keys.add(emailKey);
      if (idKey) keys.add(idKey);
    });
    return keys;
  }, [activeSubscriptions]);

  const subscriptionAttendanceKeys = useMemo(() => {
    const keys = new Set();
    activeSubscriptions.forEach((item) => {
      const key = normalizeKey(
        item.email || item.userEmail || item.userId || item.uid || item.id
      );
      if (key) keys.add(key);
    });
    return Array.from(keys);
  }, [activeSubscriptions]);

  const filteredParticipants = useMemo(() => {
    if (!participants.length) return [];
    return participants.filter((p) => {
      const emailKey = normalizeKey(p.email || p.userEmail);
      const idKey = normalizeKey(p.userId || p.uid || p.id);
      return !subscriptionKeys.has(emailKey) && !subscriptionKeys.has(idKey);
    });
  }, [participants, subscriptionKeys]);

  const inscrisiKeys = useMemo(() => {
    const keys = new Set();
    filteredParticipants.forEach((p) => {
      const key = normalizeKey(
        p.email || p.userEmail || p.userId || p.uid || p.id
      );
      if (key) keys.add(key);
    });
    return Array.from(keys);
  }, [filteredParticipants]);

  const summaryTotal = useMemo(() => {
    const prezentiInscrisi = inscrisiKeys.filter(
      (key) => attendance[key] === "prezent"
    ).length;
    const absentiInscrisi = inscrisiKeys.filter(
      (key) => attendance[key] === "absent"
    ).length;
    const prezentiSub = subscriptionAttendanceKeys.filter(
      (key) => attendance[key] === "prezent"
    ).length;
    const absentiSub = subscriptionAttendanceKeys.filter(
      (key) => attendance[key] === "absent"
    ).length;

    return {
      prezent: prezentiInscrisi + prezentiSub,
      absent: absentiInscrisi + absentiSub,
    };
  }, [attendance, inscrisiKeys, subscriptionAttendanceKeys]);

  const openSubscriptionCalendar = async (sub) => {
    if (!sub) return;
    setCalendarSub(sub);
    setCalendarStatus({});
    setCalendarOpen(true);
    setCalendarLoading(true);

    const startNum = toDateNumber(sub.dataInceput || "");
    const endNum = toDateNumber(sub.dataFinal || sub.dataExpirare || "");
    const baseDate = startNum ? new Date(startNum) : new Date();
    setCalendarMonth({ year: baseDate.getFullYear(), month: baseDate.getMonth() });

    const statusMap = {};
    const email = normalizeKey(sub.email || sub.userEmail || "");
    const userId = sub.userId || sub.uid || "";
    const tip = (sub.tipTraseu || selectedProgram || "").toUpperCase();

    const collect = (snap) => {
      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const dateNum = toDateNumber(data.data || data.date || "");
        if (!dateNum) return;
        if (startNum && endNum && (dateNum < startNum || dateNum > endNum)) return;
        const iso = toIsoDate(data.data || data.date || "");
        if (!iso) return;
        if (!data.status) return;
        statusMap[iso] = data.status;
      });
    };

    try {
      if (email) {
        const qEmail = query(
          collection(db, "prezente"),
          where("userEmail", "==", email),
          where("tipProgram", "==", tip)
        );
        const snapEmail = await getDocs(qEmail);
        collect(snapEmail);
      }
      if (userId) {
        const qId = query(
          collection(db, "prezente"),
          where("userId", "==", userId),
          where("tipProgram", "==", tip)
        );
        const snapId = await getDocs(qId);
        collect(snapId);
      }
    } catch (err) {
      console.error("Eroare la incarcare prezente abonament:", err);
    } finally {
      setCalendarStatus(statusMap);
      setCalendarLoading(false);
    }
  };

  const closeCalendar = () => {
    setCalendarOpen(false);
    setCalendarSub(null);
    setCalendarStatus({});
  };

  const changeMonth = (delta) => {
    const next = new Date(calendarMonth.year, calendarMonth.month + delta, 1);
    setCalendarMonth({ year: next.getFullYear(), month: next.getMonth() });
  };

  const calendarDays = useMemo(() => {
    if (!calendarSub) return [];
    const { year, month } = calendarMonth;
    const firstDay = new Date(year, month, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const totalDays = new Date(year, month + 1, 0).getDate();
    const startNum = toDateNumber(calendarSub.dataInceput || "");
    const endNum = toDateNumber(
      calendarSub.dataFinal || calendarSub.dataExpirare || ""
    );
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= totalDays; d++) {
      const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(
        d
      ).padStart(2, "0")}`;
      const dateNum = new Date(year, month, d).getTime();
      const inRange =
        startNum && endNum ? dateNum >= startNum && dateNum <= endNum : false;
      const status = calendarStatus[iso];
      arr.push({ day: d, iso, inRange, status });
    }
    return arr;
  }, [calendarMonth, calendarSub, calendarStatus]);

  return (
    <div className="admin-attendance-page">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold admin-attendance-kicker">
              {ro.admin.attendance.kicker}
            </p>
            <h1 className="text-3xl font-bold">
              {ro.admin.attendance.title
                .replace("{program}", titleProgram)
                .replace("{date}", titleDate)}
            </h1>
            <p className="text-sm mt-1 admin-attendance-muted">
              {ro.admin.attendance.subtitle}
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg font-semibold admin-attendance-back"
          >
            {ro.admin.attendance.back}
          </button>
        </div>

        <div className="rounded-xl p-5 grid md:grid-cols-3 gap-4 admin-attendance-card">
          <label className="flex flex-col gap-2">
            <span className="text-sm font-semibold admin-attendance-kicker">
              {ro.admin.attendance.programLabel}
            </span>
            <select
              className="p-3 rounded-lg border themed-select"
              value={selectedProgram}
              onChange={(e) => handleProgramChange(e.target.value)}
            >
              <option value="">{ro.admin.attendance.programPlaceholder}</option>
              {programOptions.map((tip) => (
                <option key={tip} value={tip}>
                  {tip}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-sm font-semibold admin-attendance-kicker">
              {ro.admin.attendance.dateLabel}
            </span>
            <select
              className="p-3 rounded-lg border themed-select"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              disabled={!selectedProgram}
            >
              <option value="">{ro.admin.attendance.datePlaceholder}</option>
              {(datesByProgram[selectedProgram] || []).map((d) => (
                <option key={d} value={d}>
                  {toDisplayDate(d)}
                </option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-2 items-end">
            <div className="admin-attendance-stat">
              <p className="admin-attendance-stat-label">
                {ro.admin.attendance.presentLabel}
              </p>
              <p className="admin-attendance-stat-value is-prezent">
                {summaryTotal.prezent}
              </p>
            </div>
            <div className="admin-attendance-stat">
              <p className="admin-attendance-stat-label">
                {ro.admin.attendance.absentLabel}
              </p>
              <p className="admin-attendance-stat-value is-absent">
                {summaryTotal.absent}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl p-5 space-y-3 admin-attendance-card">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {ro.admin.attendance.participantsTitle} ({filteredParticipants.length})
            </h2>
            {loadingList && (
              <span className="text-sm" style={{ color: "#6b7280" }}>
                {ro.admin.attendance.loading}
              </span>
            )}
          </div>

          {filteredParticipants.length === 0 && !loadingList && (
            <p className="admin-attendance-muted">{ro.admin.attendance.empty}</p>
          )}

          <div className="space-y-3">
            {filteredParticipants.map((p) => {
              const key = normalizeKey(
                p.email || p.userEmail || p.userId || p.id
              );
              const status = attendance[key];
              return (
                <div
                  key={p.id || key}
                  className="p-4 rounded-lg flex items-center justify-between gap-3 admin-attendance-row"
                >
                  <div className="flex flex-col">
                    <p className="font-semibold text-lg">
                      {p.numePrenume ||
                        p.nume ||
                        ro.admin.attendance.participantFallback}
                    </p>
                    <p className="text-sm admin-attendance-muted">
                      {p.email || p.userEmail || ro.admin.attendance.emailFallback}
                    </p>
                  </div>

                  <div className="flex gap-2 admin-attendance-actions">
                    <button
                      className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-1 ${statusClasses(
                        status === "prezent",
                        "prezent"
                      )}`}
                      onClick={() =>
                        status === "prezent"
                          ? clearAttendance(p)
                          : upsertAttendance(p, "prezent")
                      }
                    >
                      <span>✔</span> {ro.admin.attendance.present}
                    </button>
                    <button
                      className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-1 ${statusClasses(
                        status === "absent",
                        "absent"
                      )}`}
                      onClick={() =>
                        status === "absent"
                          ? clearAttendance(p)
                          : upsertAttendance(p, "absent")
                      }
                    >
                      <span>✖</span> {ro.admin.attendance.absent}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl p-5 space-y-3 admin-attendance-card">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {ro.admin.attendance.subscriptionsTitle} ({activeSubscriptions.length})
            </h2>
            {loadingSubscriptions && (
              <span className="text-sm" style={{ color: "#6b7280" }}>
                {ro.admin.attendance.loading}
              </span>
            )}
          </div>

          {activeSubscriptions.length === 0 && !loadingSubscriptions && (
            <p className="admin-attendance-muted">
              {ro.admin.attendance.subscriptionsEmpty}
            </p>
          )}

          <div className="space-y-3">
            {activeSubscriptions.map((sub) => {
              const key = normalizeKey(
                sub.email || sub.userEmail || sub.userId || sub.id
              );
              const status = attendance[key];
              const displayName =
                sub.userName || sub.nume || ro.admin.attendance.participantFallback;
              const displayEmail =
                sub.email || sub.userEmail || ro.admin.attendance.emailFallback;
              const endNum = toDateNumber(sub.dataFinal || sub.dataExpirare || "");
              const now = new Date();
              now.setHours(0, 0, 0, 0);
              const daysLeft =
                endNum != null
                  ? Math.ceil((endNum - now.getTime()) / 86400000)
                  : null;
              const isExpiring = daysLeft != null && daysLeft <= 3 && daysLeft >= 0;

              return (
                <div
                  key={sub.id || key}
                  className="p-4 rounded-lg flex items-center justify-between gap-3 admin-attendance-row"
                >
                  <div className="flex flex-col">
                    <p className="font-semibold text-lg">{displayName}</p>
                    <p className="text-sm admin-attendance-muted">{displayEmail}</p>
                  </div>

                  <div className="flex gap-2 admin-attendance-actions">
                    <button
                      className={`attendance-pill attendance-alert ${
                        isExpiring ? "is-expiring" : "is-active"
                      }`}
                      onClick={() => openSubscriptionCalendar(sub)}
                      aria-label="Calendar abonament"
                      title="Calendar abonament"
                    >
                      <span className="attendance-alert-icon">
                        {isExpiring ? "!" : "★"}
                      </span>
                    </button>
                    <button
                      className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-1 ${statusClasses(
                        status === "prezent",
                        "prezent"
                      )}`}
                      onClick={() => {
                        const payload = {
                          email: sub.email || sub.userEmail,
                          userEmail: sub.email || sub.userEmail,
                          uid: sub.userId,
                          userId: sub.userId,
                          numePrenume: displayName,
                        };
                        if (status === "prezent") {
                          clearAttendance(payload);
                        } else {
                          upsertAttendance(payload, "prezent");
                        }
                      }}
                    >
                      <span>✔</span> {ro.admin.attendance.present}
                    </button>
                    <button
                      className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-1 ${statusClasses(
                        status === "absent",
                        "absent"
                      )}`}
                      onClick={() => {
                        const payload = {
                          email: sub.email || sub.userEmail,
                          userEmail: sub.email || sub.userEmail,
                          uid: sub.userId,
                          userId: sub.userId,
                          numePrenume: displayName,
                        };
                        if (status === "absent") {
                          clearAttendance(payload);
                        } else {
                          upsertAttendance(payload, "absent");
                        }
                      }}
                    >
                      <span>✖</span> {ro.admin.attendance.absent}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {calendarOpen && calendarSub && (
        <div
          className="attendance-calendar-overlay"
          style={{ backgroundColor: "var(--drawer-overlay)" }}
        >
          <div className="drawer-card drawer-modal attendance-calendar-modal w-full max-w-2xl p-6 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="drawer-kicker" style={{ color: "var(--color-accent)" }}>
                  Calendar abonament
                </p>
                <h3 className="text-2xl font-bold">
                  {calendarSub.userName ||
                    calendarSub.nume ||
                    ro.admin.attendance.participantFallback}
                </h3>
                <p className="drawer-muted mt-1">
                  {calendarSub.email ||
                    calendarSub.userEmail ||
                    ro.admin.attendance.emailFallback}
                </p>
                <p className="drawer-muted mt-1">
                  {toDisplayDate(calendarSub.dataInceput)} â€“{" "}
                  {toDisplayDate(calendarSub.dataFinal || calendarSub.dataExpirare)}
                </p>
              </div>
              <button
                className="drawer-close"
                onClick={closeCalendar}
                aria-label="Inchide calendar"
              >
                X
              </button>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <button
                onClick={() => changeMonth(-1)}
                className="drawer-close attendance-calendar-arrow"
                aria-label="Luna anterioara"
              >
                {"<"}
              </button>
              <p className="text-lg font-semibold">
                {ro.profile.attendance.months[calendarMonth.month]}{" "}
                {calendarMonth.year}
              </p>
              <button
                onClick={() => changeMonth(1)}
                className="drawer-close attendance-calendar-arrow"
                aria-label="Luna urmatoare"
              >
                {">"}
              </button>
            </div>

            {calendarLoading && (
              <p className="drawer-muted">{ro.admin.attendance.loading}</p>
            )}

            <div className="grid grid-cols-7 gap-2 text-center text-sm">
              {ro.profile.attendance.weekdays.map((wd) => (
                <div
                  key={wd}
                  className="font-semibold"
                  style={{ color: "var(--color-secondary-text)" }}
                >
                  {wd}
                </div>
              ))}
              {calendarDays.map((cell, idx) => {
                if (!cell) return <div key={`empty-${idx}`} />;
                let background = "var(--drawer-card)";
                let color = "var(--color-text)";
                if (cell.status === "prezent") {
                  background = "var(--color-success-soft)";
                  color = "var(--color-success)";
                } else if (cell.status === "absent") {
                  background = "var(--color-danger-soft)";
                  color = "var(--color-danger)";
                } else if (cell.inRange) {
                  background = "rgba(249,115,22,0.15)";
                  color = "#f97316";
                }
                return (
                  <div
                    key={cell.iso}
                    className="rounded-lg font-semibold"
                    style={{
                      padding: "12px 0",
                      border: "1px solid var(--drawer-border)",
                      backgroundColor: background,
                      color,
                    }}
                  >
                    {cell.day}
                  </div>
                );
              })}
            </div>

            <button onClick={closeCalendar} className="drawer-cta secondary">
              Inchide
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAttendancePage;
